import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FunctionComponent,
} from 'react';
import FocusLock from 'react-focus-lock';
import { useIntl } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { useMemberId } from '@trello/authentication';
import { dontUpsell } from '@trello/browser';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { isPaidLicensedEnterpriseMember } from '@trello/business-logic/member';
import { useFeatureGate } from '@trello/feature-gate-client';
import { showFlag } from '@trello/nachos/experimental-flags';
import { Overlay } from '@trello/nachos/overlay';
import { useRouteId } from '@trello/router';
import { RouteId } from '@trello/router/routes';

import { MultiWorkspacePlanSelectionOverlay } from 'app/src/components/FreeTrial';
import { isEligibleForTrial } from 'app/src/components/FreeTrial/useStartFreeTrialForOrg';
import { useEligibleWorkspacesToUpgradeQuery } from 'app/src/components/SelectWorkspaceToUpgrade';
import { useIsPaidUser } from '../useIsPaidUser';
import { usePersonalProductivityCohortValue } from '../usePersonalProductivityCohortValue';
import { GAWelcomeModalWithTour } from './GAWelcomeModalWithTour';
import { GAWelcomeModalWithTrial } from './GAWelcomeModalWithTrial';
import { GAWelcomeModalWithUpgrade } from './GAWelcomeModalWithUpgrade';
import { useMemberEnterpriseLicensesFragment } from './MemberEnterpriseLicensesFragment.generated';

import * as styles from './GAWelcomeModal.module.less';

export const GAWelcomeModal: FunctionComponent = () => {
  const [isModalOrFlagOpen, setIsModalOrFlagOpen] = useState(true);
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
  const intl = useIntl();
  const { value: isGateEnabled } = useFeatureGate('trello_beta_ga');
  const { value: isOutlookSpotlightEnabled } = useFeatureGate(
    'trello_outlook_announcement',
  );
  const { dismissOneTimeMessage, isOneTimeMessageDismissed } =
    useOneTimeMessagesDismissed();
  const memberId = useMemberId();
  const isPaidUser = useIsPaidUser();
  const personalProductivityCohort = usePersonalProductivityCohortValue();
  const routeId = useRouteId();
  const isBoardRoute = routeId === RouteId.BOARD;

  const hasSeenOnboarding = isOneTimeMessageDismissed(
    'split-screen-onboarding',
  );
  const gaViaDirectSignup = isOneTimeMessageDismissed('new-user-onboarding');

  const showModalOrFlag =
    isModalOrFlagOpen &&
    !isUpsellModalOpen &&
    !gaViaDirectSignup &&
    !isOneTimeMessageDismissed('beta-ga-welcome-modal') &&
    isGateEnabled &&
    personalProductivityCohort === 'ga' &&
    isBoardRoute;

  const { workspaces: eligibleWorkspaces, loading: loadingEligibleWorkspaces } =
    useEligibleWorkspacesToUpgradeQuery({
      skip: !showModalOrFlag || isPaidUser,
    });

  const { data: member } = useMemberEnterpriseLicensesFragment({
    from: { id: memberId },
  });

  const isEnterpriseMember = isPaidLicensedEnterpriseMember({
    enterpriseLicenses: member?.enterpriseLicenses,
  });

  const cannotUpgradeOrStartTrial =
    eligibleWorkspaces.length === 0 && !loadingEligibleWorkspaces;

  const shouldShowFlag =
    showModalOrFlag &&
    hasSeenOnboarding &&
    (isPaidUser || dontUpsell() || cannotUpgradeOrStartTrial) &&
    !isEnterpriseMember;

  const shouldShowModal =
    showModalOrFlag && !shouldShowFlag && !loadingEligibleWorkspaces;

  const isFreeTrialEligible = useMemo(
    () => Boolean(eligibleWorkspaces?.some(isEligibleForTrial)),
    [eligibleWorkspaces],
  );

  const onClose = useCallback(() => {
    setIsModalOrFlagOpen(false);
    setIsUpsellModalOpen(false);
    dismissOneTimeMessage('beta-ga-welcome-modal');

    /**
     * We will not announce Outlook support to those transitioning to GA after
     * Outlook support is released. Starting at the release of Outlook support,
     * the announcement will be dismissed by Beta -> GA onboarding here.
     */
    if (isOutlookSpotlightEnabled) {
      dismissOneTimeMessage('outlook-announcement-spotlight');
    }
  }, [dismissOneTimeMessage, isOutlookSpotlightEnabled]);

  const onContinueToUpsell = useCallback(() => {
    setIsUpsellModalOpen(true);
  }, []);

  useEffect(() => {
    if (shouldShowFlag) {
      showFlag({
        id: 'gaWelcomeFlag',
        title: intl.formatMessage({
          id: 'templates.beta_ga.the-new-trello-is-officially',
          defaultMessage: 'The new Trello is officially here!',
          description: 'Flag for GA welcome',
        }),
        description: intl.formatMessage({
          id: 'templates.beta_ga.trello-beta-has-ended',
          defaultMessage:
            'Trello Beta has ended, and the new Trello is now live for everyone.',
          description: 'Flag for GA welcome',
        }),
        appearance: 'success',
      });
      Analytics.sendViewedComponentEvent({
        componentType: 'flag',
        componentName: 'gaWelcomeFlag',
        source: 'boardScreen',
      });
      onClose();
    }
  }, [intl, onClose, shouldShowFlag]);

  const ModalComponent = useMemo(() => {
    let modal;

    if (!hasSeenOnboarding || isEnterpriseMember) {
      modal = <GAWelcomeModalWithTour onClose={onClose} />;
    } else if (isFreeTrialEligible) {
      modal = (
        <GAWelcomeModalWithTrial
          onClose={onClose}
          onContinue={onContinueToUpsell}
        />
      );
    } else {
      modal = (
        <GAWelcomeModalWithUpgrade
          onClose={onClose}
          onContinue={onContinueToUpsell}
        />
      );
    }

    return modal;
  }, [
    hasSeenOnboarding,
    isEnterpriseMember,
    isFreeTrialEligible,
    onClose,
    onContinueToUpsell,
  ]);

  return isUpsellModalOpen ? (
    <MultiWorkspacePlanSelectionOverlay
      analyticsAttributes={{ upsell: 'GAWelcomeModal' }}
      currentPlanSelected="premium"
      onClose={onClose}
      onCompleteUpsell={onClose}
    />
  ) : (
    shouldShowModal && (
      <Overlay
        className={styles.overlay}
        closeOnEscape={true}
        closeOnOutsideClick={false}
        onClose={onClose}
      >
        <FocusLock autoFocus={true} returnFocus={true}>
          {ModalComponent}
        </FocusLock>
      </Overlay>
    )
  );
};
